import { getLayerTemplate, updateLayerTemplate, updateLayerTemplateProperties } from '@/api/layerTemplates';
import { getLayer } from '@/api/layers';
import { LayerPropertiesByCategory } from '@/components/Layers/LayerPropertiesByCategory';
import { FieldLayerPrefillSelects } from '@/components/Templates/FieldLayerPrefillSelects';
import { LayerTemplateDetail, getLayerTypeLabel } from '@/types/layerTemplate';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import { Download as DownloadIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';

interface LayerTemplateDetailModalProps {
  open: boolean;
  onClose: () => void;
  templateId: string | null;
  onTemplateUpdated?: () => void;
}

export const LayerTemplateDetailModal: React.FC<LayerTemplateDetailModalProps> = ({
  open,
  onClose,
  templateId,
  onTemplateUpdated,
}) => {
  const [template, setTemplate] = useState<LayerTemplateDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';

  // Visibility status UUIDs
  const PUBLIC_STATUS_UUID = '53184dbd-ff42-497e-8137-a89dda5e5f87';
  const COMPANY_PUBLIC_UUID = '20a7f07b-93c4-4188-93c1-920bbc8796c8';
  const PRIVATE_UUID = '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9';

  const [editData, setEditData] = useState({
    name: '',
    description: '',
    type: '',
    is_public: false,
    visibility_status_id: PRIVATE_UUID,
  });
  const [propertiesData, setPropertiesData] = useState<{ [key: string]: string | number }>({});
  const [selectedLayerId, setSelectedLayerId] = useState<string>('');

  useEffect(() => {
    if (open && templateId) {
      loadTemplate();
    }
  }, [open, templateId]);

  const loadTemplate = async () => {
    if (!templateId) return;

    setLoading(true);
    setError(null);

    try {
      const templateData = await getLayerTemplate(templateId);

      if (!templateData) {
        throw new Error('Template data is undefined');
      }

      setTemplate(templateData);
      setEditData({
        name: templateData.name || '',
        description: templateData.description || '',
        type: templateData.type || '',
        is_public: templateData.is_public || false,
        visibility_status_id: templateData.visibility_status_id || PRIVATE_UUID,
      });

      // Initialize properties data
      const initialProperties: { [key: string]: string | number } = {};
      if (templateData.properties) {
        templateData.properties.forEach(prop => {
          // Always add the property, even if value is null/undefined
          initialProperties[prop.name] = prop.value !== null && prop.value !== undefined ? prop.value : '';
        });
      }
      setPropertiesData(initialProperties);
    } catch (err) {
      console.error('Error loading template:', err);
      setError(err instanceof Error ? err.message : 'Failed to load template details');
    } finally {
      setLoading(false);
    }
  };

  const handleLayerSelect = async (layerId: string) => {
    setSelectedLayerId(layerId);

    if (!layerId) {
      return; // If no layer selected, do nothing
    }

    try {
      // Get details of selected layer
      const layerDetail = await getLayer(layerId);

      // Pre-fill template attributes with values from layer
      const newPropertiesData: { [key: string]: string | number } = {};

      if (layerDetail.properties) {
        layerDetail.properties.forEach(layerProp => {
          // Find corresponding attribute in template
          const templateProp = template?.properties.find(tp => tp.name === layerProp.name);
          if (templateProp) {
            // If layer has value, use it, otherwise leave empty
            newPropertiesData[layerProp.name] = layerProp.value !== null && layerProp.value !== undefined ? layerProp.value : '';
          }
        });
      }

      // Update all template properties
      const updatedPropertiesData: { [key: string]: string | number } = {};
      if (template?.properties) {
        template.properties.forEach(templateProp => {
          // If there's value from layer, use it, otherwise keep current value or empty string
          updatedPropertiesData[templateProp.name] = newPropertiesData[templateProp.name] !== undefined
            ? newPropertiesData[templateProp.name]
            : (propertiesData[templateProp.name] || '');
        });
      }

      setPropertiesData(updatedPropertiesData);
    } catch (err) {
      console.error('Error loading layer details:', err);
      setError('Failed to load layer details');
    }
  };

  const handleSave = async () => {
    if (!template) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Current propertiesData:', propertiesData);

      // Update template basic info
      await updateLayerTemplate(template.id, {
        name: editData.name,
        description: editData.description,
        type: editData.type,
        is_public: editData.is_public,
        visibility_status_id: editData.visibility_status_id,
      });

      console.log('After updateLayerTemplate, propertiesData still:', propertiesData);

      // Send all form properties; empty → null so backend deletes sparse rows
      const propertiesUpdate = Object.entries(propertiesData)
        .map(([name, value]) => {
          const property = template.properties.find(p => p.name === name);
          if (!property || !property.id) {
            return null;
          }
          const isEmpty = value === null || value === undefined || value === '';
          return {
            attribute_id: property.id,
            value: isEmpty ? null : value,
          };
        })
        .filter((p): p is { attribute_id: string; value: string | number | null } =>
          p !== null && !!p.attribute_id
        );

      console.log('Properties update payload:', propertiesUpdate);
      console.log('About to call updateLayerTemplateProperties with payload:', propertiesUpdate);

      if (propertiesUpdate.length > 0) {
        await updateLayerTemplateProperties(template.id, propertiesUpdate);
      }

      // Reload template data to get the latest state
      const reloadedTemplate = await getLayerTemplate(template.id);
      setTemplate(reloadedTemplate);

      // Update properties data with reloaded data
      const updatedProperties: { [key: string]: string | number } = {};
      if (reloadedTemplate.properties) {
        reloadedTemplate.properties.forEach(prop => {
          if (prop.value !== null && prop.value !== undefined) {
            updatedProperties[prop.name] = prop.value;
          }
        });
      }
      setPropertiesData(updatedProperties);

      setEditMode(false);
      console.log('Template updated successfully, calling onTemplateUpdated');
      onTemplateUpdated?.();
    } catch (err) {
      console.error('Error updating template:', err);
      setError(err instanceof Error ? err.message : 'Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (template) {
      setEditData({
        name: template.name || '',
        description: template.description || '',
        type: template.type || '',
        is_public: template.is_public || false,
        visibility_status_id: template.visibility_status_id || PRIVATE_UUID,
      });

      // Reset properties data
      const initialProperties: { [key: string]: string | number } = {};
      if (template.properties) {
        template.properties.forEach(prop => {
          // Always add the property, even if value is null/undefined
          initialProperties[prop.name] = prop.value !== null && prop.value !== undefined ? prop.value : '';
        });
      }
      setPropertiesData(initialProperties);
    }
    setSelectedLayerId(''); // Reset selected layer
    setEditMode(false);
  };

  const handlePropertyChange = (propertyName: string, value: string | number) => {
    console.log('Property change:', propertyName, '=', value);
    setPropertiesData(prev => {
      const updated = {
        ...prev,
        [propertyName]: value,
      };
      console.log('Updated propertiesData:', updated);
      return updated;
    });
  };

  const escapeCsvCell = (value: string): string => {
    if (value.includes('"') || value.includes(',') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const exportTemplateToCsv = () => {
    if (!template) {
      return;
    }

    const lines = ['attribute,value'];
    template.properties.forEach((prop) => {
      const attribute = prop.name ?? '';
      const rawValue = prop.value === null || prop.value === undefined ? '' : String(prop.value);
      lines.push(`${escapeCsvCell(attribute)},${escapeCsvCell(rawValue)}`);
    });

    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const safeName = (template.name || 'template')
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, '_');

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${safeName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };



  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Oil':
        return 'success';
      case 'Gas':
        return 'info';
      case 'Oil & Gas':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (!template && !loading) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {editMode ? 'Edit Reservoir Template' : 'Reservoir Template Details'}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            {template && !editMode && (
              <Button
                onClick={exportTemplateToCsv}
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
              >
                Export CSV
              </Button>
            )}
            {template && (
              <>
                <Chip
                  icon={
                    template.visibility_status_id === PUBLIC_STATUS_UUID ? <span>🌐</span> :
                    template.visibility_status_id === COMPANY_PUBLIC_UUID ? <span>🏢</span> :
                    <span>🔒</span>
                  }
                  label={
                    template.visibility_status_id === PUBLIC_STATUS_UUID ? 'Public' :
                    template.visibility_status_id === COMPANY_PUBLIC_UUID ? 'Company Public' :
                    'Private'
                  }
                  color={
                    template.visibility_status_id === PUBLIC_STATUS_UUID ? 'success' :
                    template.visibility_status_id === COMPANY_PUBLIC_UUID ? 'info' :
                    'default'
                  }
                  size="small"
                />
                <Chip
                  label={getLayerTypeLabel(template.type)}
                  color={getTypeColor(getLayerTypeLabel(template.type)) as any}
                  size="small"
                />
              </>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && !template ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : template ? (
          <Box>
            {/* Template Information */}
            <Typography variant="h6" gutterBottom>
              Template Information
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={editData.name}
                  onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={editData.type}
                    label="Type"
                    onChange={(e) => setEditData(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="oil">Oil</MenuItem>
                    <MenuItem value="gas">Gas</MenuItem>
                    <MenuItem value="oil_and_gas">Oil & Gas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={editData.visibility_status_id || PRIVATE_UUID}
                    label="Visibility"
                    onChange={(e) => {
                      const selectedId = e.target.value as string;
                      setEditData(prev => ({
                        ...prev,
                        visibility_status_id: selectedId,
                        is_public: selectedId === PUBLIC_STATUS_UUID  // Keep for backward compatibility
                      }));
                    }}
                    disabled={!editMode}
                  >
                    <MenuItem value={PRIVATE_UUID}>Private</MenuItem>
                    <MenuItem value={COMPANY_PUBLIC_UUID}>Company Public</MenuItem>
                    {isSuperAdmin && (
                      <MenuItem value={PUBLIC_STATUS_UUID}>Public</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="Describe when and how to use this template"
                  multiline
                  minRows={3}
                  value={editData.description}
                  onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>

            {/* Layer Selection for Pre-filling */}
            {editMode && (
              <>
                <Typography variant="h6" gutterBottom>
                  Pre-fill from Layer
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose a field, then a layer to pre-fill template attributes. You can still edit values after selection.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <FieldLayerPrefillSelects
                    selectedLayerId={selectedLayerId}
                    onLayerSelect={handleLayerSelect}
                    disabled={loading}
                  />
                </Box>
              </>
            )}

            {/* Template Metadata */}
            <Typography variant="h6" gutterBottom>
              Template Metadata
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Author"
                  value={template.author_name || 'Unknown'}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Editor"
                  value={template.editor_name || 'Unknown'}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Created"
                  value={new Date(template.created_at).toLocaleDateString()}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Updated"
                  value={new Date(template.updated_at).toLocaleDateString()}
                  disabled
                  size="small"
                />
              </Grid>
            </Grid>

            {/* Properties */}
            <Typography variant="h6" gutterBottom>
              Template Properties
            </Typography>

            <Box sx={{ maxHeight: 600, overflow: 'auto', mt: 2 }}>
              <LayerPropertiesByCategory
                properties={template.properties.map(prop => ({
                  id: prop.id,
                  name: prop.name,
                  unit: prop.unit,
                  value_type: prop.type,
                  description: prop.description || null,
                }))}
                propertiesData={propertiesData}
                onPropertyChange={handlePropertyChange}
                disabled={!editMode}
                readOnly={!editMode}
              />
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>

        {template && (
          <>
            {editMode ? (
              <>
                <Button onClick={handleCancel} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={loading}
                >
                  Save
                </Button>
              </>
            ) : (
              (() => {
                // Check if template is public and user is not super admin
                const isPublic = template.visibility_status_id === PUBLIC_STATUS_UUID;
                const canEdit = !isPublic || isSuperAdmin;
                const isFromUserOrg = template.organization_id === user?.organization_id;

                // Can edit if: template is from user's org OR (template is public AND user is super admin)
                const hasEditPermission = isFromUserOrg || (isPublic && isSuperAdmin);

                if (!hasEditPermission) {
                  return null; // Don't show edit button for public templates if user is not super admin
                }

                return (
                  <Button
                    onClick={() => setEditMode(true)}
                    variant="contained"
                  >
                    Edit
                  </Button>
                );
              })()
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
